import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DwCacheService, DwConfigService, DwConfigServiceToken, DwEventService, DwFieldAggregate, DwFieldType, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwQuery, DwQueryHelper, DwQueryMetaData, DwQueryResult, DwSecurityTopics, DwSecurityUserService } from "@devwareapps/devware-cap";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { AppMetaData, FlightSchoolGroupStudentEntity, InstructorLeaderboardQueryBuilder, InstructorLeaderboardQueryEntity, InstructorStudentEntity, RoSchoolCourseEnrollmentStatsEntity, RoSchoolCourseEnrollmentStatsQueryBuilder, StudentCourseEntity } from "../../../meta-data/app-meta-data.service";
import { Observable, of } from "rxjs";
import { SchoolCourseStatAggregateFields, SchoolCourseStatEntity } from "../models/stat-entities/school-course-stat-entity.model";
import { AdminManagerService } from "../../admin/services/admin-manager.service";
import { SchoolReportingFilters } from "../models/school-reporting-filters.model";
import { SchoolStatAggregateFields, SchoolStatEntity } from "../models/stat-entities/school-stat-entity.model";
import { SchoolAssignmentStatAggregateFields, SchoolAssignmentStatEntity } from "../models/stat-entities/school-assignment-stat-entity.model";
import { map, mergeMap } from "rxjs/operators";
import { InstructorAssignmentStatAggregateFields, InstructorStatAggregateFields, InstructorStatEntity } from "../models/stat-entities/instructor-stat-entity.model";
import { ModuleOverviewAggregateFields, ModuleOverviewEntity } from "../models/stat-entities/module-overview-entity.model";

@Injectable({
    providedIn: 'root'
})
export class ReportingRepositoryService {
    

    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private dwEventService: DwEventService,
        private router: Router,
        private http: HttpClient,
        private dateTimeUtilServie: DateTimeUtilService,
        private adminManagerService: AdminManagerService
    ) {

    }

    updateHideStudentCourseFromReporting(StudentCourseId: number, hideInReporting: boolean): Observable<StudentCourseEntity> {
        const studentCourse = AppMetaData.StudentCourse.CreateEntity();

        studentCourse.StudentCourseId = StudentCourseId;
        studentCourse.HideInReporting = hideInReporting;
        return this.dwOrmDataService.saveEntity(studentCourse);
    }

    getCourseEnrollmentStats(filters: SchoolReportingFilters): Observable<SchoolCourseStatEntity[]> {
        const query = this.buildSchoolCourseEnrollmentQuery(filters);
        query.query.FieldSettings.AggregateQuery = true;
        // Add Group by Fields
        query.addFields(f => [f.FlightSchoolId, f.CourseId, f.CourseName]);

        // Add Aggregate Fields
        const attrs = AppMetaData.RoSchoolCourseEnrollmentStats.Attributes;

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolCourseStatAggregateFields.ActiveStudents,
            FullFieldName: attrs.ActiveStudent.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolCourseStatAggregateFields.CompletedStudents,
            FullFieldName: attrs.IsComplete.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolCourseStatAggregateFields.AverageCompletionTime,
            FullFieldName: attrs.CompletedTotalTimeSeconds.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolCourseStatAggregateFields.AverageProgress,
            FullFieldName: attrs.InProgressPercentComplete.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolCourseStatAggregateFields.AverageProgress,
            FullFieldName: attrs.ProgressDaysLearningLastActivity.fieldNameWithItemPrefix
        });

        return this.dwOrmDataService.executeQuery<SchoolCourseStatEntity>(query);
    }

    getDrillDownReferenceData(filters: SchoolReportingFilters): Observable<RoSchoolCourseEnrollmentStatsEntity[]> {
        const query = this.buildSchoolCourseEnrollmentQuery(filters);

        return this.dwOrmDataService.executeQuery<RoSchoolCourseEnrollmentStatsEntity>(query);
    }

    getSchoolStats(filters: SchoolReportingFilters, includeAssignmentStats: boolean = true): Observable<SchoolStatEntity> {
        const query = this.buildSchoolCourseEnrollmentQuery(filters);

        query.query.FieldSettings.AggregateQuery = true;

        // Add Group by Fields
        query.addFields(f => [f.FlightSchoolId]);

        // Add Aggregate Fields
        const attrs = AppMetaData.RoSchoolCourseEnrollmentStats.Attributes;

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolStatAggregateFields.ActiveStudents,
            FullFieldName: attrs.ActiveStudent.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolStatAggregateFields.CompletedStudents,
            FullFieldName: attrs.IsComplete.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolStatAggregateFields.AverageProgress,
            FullFieldName: attrs.InProgressTotalTimeSeconds.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolStatAggregateFields.AverageCompletionTime,
            FullFieldName: attrs.CompletedTotalTimeSeconds.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolStatAggregateFields.ACSAverageFirstTimePassRate,
            FullFieldName: attrs.AcsTestPassedFirstTime.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolStatAggregateFields.ACSAwaitingUpload,
            FullFieldName: attrs.AcsAwaitingUpload.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolStatAggregateFields.ACSTestUploaded,
            FullFieldName: attrs.AcsTestUploaded.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolStatAggregateFields.ACSTestAverageScore,
            FullFieldName: attrs.AcsTestAverageScore.fieldNameWithItemPrefix
        });


        return this.dwOrmDataService.executeQuerySingle<SchoolStatEntity>(query)
            .pipe(mergeMap(schoolStats => {
                if (includeAssignmentStats && schoolStats) {
                    return this.getSchoolAssignmentStats(filters)
                        .pipe(map(assignmentStats => {
                            schoolStats.RoSchoolAssignmentStats = assignmentStats;
                            return schoolStats;
                        }));
                } else {
                    return of(schoolStats);
                }
            }));
    }

    getAtRiskStudents(filters: SchoolReportingFilters, maxToShow?: number): Observable<RoSchoolCourseEnrollmentStatsEntity[]> {
        const query = this.buildSchoolCourseEnrollmentQuery(filters, true);

        // Base filters
        query.addFilterAnd(f => f.LessThan(a => a.PercentComplete, 100));
        query.addFilterAnd(f => f.IsNotBlank(a => a.FirstActivityDateTime, null));

        if (maxToShow && maxToShow > 0) {
            query.query.PagingInfo = {
                PageNo: 1,
                PageSize: maxToShow,
            };
        }

        return this.dwOrmDataService.executeQuery(query);
    }

    // getNoActivityStudents(filters: SchoolReportingFilters, maxToShow?: number): Observable<RoSchoolCourseEnrollmentStatsEntity[]> {
    //     const query = this.buildSchoolCourseEnrollmentQuery(filters);

    //     // Base filters
    //     query.addFilterAnd(f => f.LessThan(a => a.PercentComplete, 100));
    //     query.addFilterAnd(f => f.IsNotBlank(a => a.FirstActivityDateTime, null));
    //     query.addFilterAnd(f => f.DateFilters.LessThanDays(a => a.LastActivityDateTime, 7));

    //     if (maxToShow && maxToShow > 0) {
    //         query.query.PagingInfo = {
    //             PageNo: 1,
    //             PageSize: maxToShow,
    //         };
    //     }

    //     query.addOrderBy(a => a.LastActivityDateTime);

    //     return this.dwOrmDataService.executeQuery(query);
    // }

    getNoActivityStudents(filters: SchoolReportingFilters, maxToShow?: number): Observable<DwQueryResult<RoSchoolCourseEnrollmentStatsEntity>> {
        const query = this.buildSchoolCourseEnrollmentQuery(filters);

        // Base filters
        query.addFilterAnd(f => f.LessThan(a => a.PercentComplete, 100));
        query.addFilterAnd(f => f.IsNotBlank(a => a.FirstActivityDateTime, null));
        query.addFilterAnd(f => f.IsNotBlank(a => a.LastActivityDateTime, null));
        query.addFilterAnd(f => f.DateFilters.LessThanDays(a => a.LastActivityDateTime, 7));

        if (maxToShow && maxToShow > 0) {
            query.query.PagingInfo = {
                PageNo: 1,
                PageSize: maxToShow,
                ReturnCount: true
            };
        }

        query.addOrderBy(a => a.LastActivityDateTime);

        return this.dwOrmDataService.executePagedQuery(query);
    }

    private getSchoolAssignmentStats(filters: SchoolReportingFilters): Observable<SchoolAssignmentStatEntity> {
        const query = AppMetaData.RoSchoolAssignmentStats.CreateQueryBuilder();

        const aviatorUser = this.adminManagerService.getAviatorUser();

        query.query.FieldSettings.AggregateQuery = true;

        // Always filter by Flight School
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId));

        // Apply additional filters
        if (filters.courseId) {
            query.addFilterAnd(f => f.Equal(a => a.CourseId, filters.courseId));
        }

        if (filters.studentId) {
            query.addFilterAnd(f => f.Equal(a => AppMetaData.AssignmentStudent.Attributes.StudentId, filters.studentId));
        }

        if (filters.instructorStudentIds && filters.instructorStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => AppMetaData.AssignmentStudent.Attributes.StudentId, filters.instructorStudentIds));
        }

        if (filters.groupStudentIds && filters.groupStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => AppMetaData.AssignmentStudent.Attributes.StudentId, filters.groupStudentIds));
        }

        if (filters.lastActivityAfterDate) {
            query.addFilterAnd(f => f.GreaterThanEqual(a => a.AssignmentCreatedDateTime, filters.lastActivityAfterDate));
        }

        // Add Group by Fields
        query.addFields(f => [f.FlightSchoolId]);

        // Add Aggregate Fields
        const attrs = AppMetaData.RoSchoolAssignmentStats.Attributes;

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.CountDistinct,
            FieldAlias: SchoolAssignmentStatAggregateFields.ActiveAssignments,
            FullFieldName: attrs.AssignmentId.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: SchoolAssignmentStatAggregateFields.PastDueAssignments,
            FullFieldName: attrs.AssignmentPastDue.fieldNameWithItemPrefix
        });

        //return query.query;
        return this.dwOrmDataService.executeQuerySingle<SchoolAssignmentStatEntity>(query);
    }

    public getInstructorLeaderboard(filters: SchoolReportingFilters): Observable<InstructorStatEntity[]> {
        return AppMetaData.Queries.InstructorLeaderboard.CreateQueryBuilderWithMetaData(this.dwMetaDataService)
            .pipe(mergeMap(queryBuilder => {
                return this.getInstructorLeaderboardWithQuery(queryBuilder, filters);
            }));
    }

    getInstructorLeaderboardWithQuery(queryDetails: { queryMetaData: DwQueryMetaData; queryBuilder: InstructorLeaderboardQueryBuilder; }, filters: SchoolReportingFilters): Observable<InstructorStatEntity[]> {

        const qh = new DwQueryHelper();

        // Clone query so we don't modify the original 
        const coreQuery = qh.cloneQuery(queryDetails.queryBuilder.query);

        qh.clearAllFields(coreQuery);

        const query = AppMetaData.Queries.InstructorLeaderboard.CreateQueryBuilderFromQuery(coreQuery);

        //const query = new InstructorLeaderboardQueryBuilder() queryBuilder.queryBuilder;

        const aviatorUser = this.adminManagerService.getAviatorUser();

        query.query.SetupAggregateQuery();


        // Always filter by Flight School
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId));
        query.addFilterAnd(f => f.Equal(a => a.StudentStatHideInReporting, false))

        // Apply additional filters
        if (filters.courseId) {
            query.addFilterAnd(f => f.Equal(a => a.StudentStatCourseId, filters.courseId));
        }

        if (filters.studentId) {
            query.addFilterAnd(f => f.Equal(a => a.StudentStatStudentId, filters.studentId));
        }

        if (filters.instructorStudentIds && filters.instructorStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.StudentStatStudentId, filters.instructorStudentIds));
        }

        if (filters.groupStudentIds && filters.groupStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.StudentStatStudentId, filters.groupStudentIds));
        }

        if (filters.lastActivityAfterDate) {
            query.addFilterAnd(f => f.GreaterThanEqual(a => a.StudentStatLastActivityDateTime, filters.lastActivityAfterDate));
        }

        // Add Group by Fields
        query.addFields(f => [f.FlightSchoolId, f.InstructorId, f.UserId]);

        // Add Aggregate Fields
        const attrs = AppMetaData.Queries.InstructorLeaderboard.Attributes;

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: InstructorStatAggregateFields.ActiveStudents,
            FullFieldName: attrs.StudentStatActiveStudent.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: InstructorStatAggregateFields.CompletedStudents,
            FullFieldName: attrs.StudentStatIsComplete.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: InstructorStatAggregateFields.ACSTestsUploaded,
            FullFieldName: attrs.StudentStatAcsTestUploaded.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: InstructorStatAggregateFields.AverageProgress,
            FullFieldName: attrs.StudentStatInProgressTotalTimeSeconds.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolStatAggregateFields.ACSAverageFirstTimePassRate,
            FullFieldName: attrs.StudentStatAcsTestPassedFirstTime.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: SchoolStatAggregateFields.ACSTestAverageScore,
            FullFieldName: attrs.StudentStatAcsTestAverageScore.fieldNameWithItemPrefix
        });

        const assignmentStatsQuery = this.buildInstructorAssignmentStatsQuery(filters);

        query.addPrefetch(r => r.RoInstructorAssignmentsPerWeek, assignmentStatsQuery);

        //return query.query;
        return this.dwOrmDataService.executeQuery<InstructorStatEntity>(query);
    }

    public getModuleOverviewStats(filters: SchoolReportingFilters): Observable<ModuleOverviewEntity[]> {
        const query = AppMetaData.RoLessonQuizStats.CreateQueryBuilder();

        query.query.SetupAggregateQuery();

        const aviatorUser = this.adminManagerService.getAviatorUser();

        // Always filter by Flight School
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId));

        if (filters.courseId) {
            query.addFilterAnd(f => f.Equal(a => a.CourseId, filters.courseId));
        }

        if (filters.studentId) {
            query.addFilterAnd(f => f.Equal(a => a.StudentId, filters.studentId));
        }

        if (filters.instructorStudentIds && filters.instructorStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.StudentId, filters.instructorStudentIds));
        }

        if (filters.groupStudentIds && filters.groupStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.StudentId, filters.groupStudentIds));
        }

        if (filters.lastActivityAfterDate) {
            query.addFilterAnd(f => f.GreaterThanEqual(a => a.SessionEndDateTime, filters.lastActivityAfterDate));
        }

        // Add Group by Fields
        query.addFields(f => [f.FlightSchoolId, f.CourseId, f.CourseName, f.CourseModuleId, f.ModuleName]);


        // Add Aggregate Fields
        const attrs = AppMetaData.RoLessonQuizStats.Attributes;

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: ModuleOverviewAggregateFields.AverageQuizPercentage,
            FullFieldName: attrs.PercentageScore.fieldNameWithItemPrefix
        });


        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: ModuleOverviewAggregateFields.TotalPassCount,
            FullFieldName: attrs.QuizPassed.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Sum,
            FieldAlias: ModuleOverviewAggregateFields.TotalFailCount,
            FullFieldName: attrs.QuizFailed.fieldNameWithItemPrefix
        });

        // Counts all session takes
        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Count,
            FieldAlias: ModuleOverviewAggregateFields.TotalTakeCount,
            //FullFieldName: attrs.QuizFailed.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: ModuleOverviewAggregateFields.AverageTime,
            FullFieldName: attrs.TotalTimeSeconds.fieldNameWithItemPrefix
        });

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: ModuleOverviewAggregateFields.AverageTimePerSession,
            FullFieldName: attrs.SessionTimeSeconds.fieldNameWithItemPrefix
        });

        return this.dwOrmDataService.executeQuery<ModuleOverviewEntity>(query);
    }

    private buildInstructorAssignmentStatsQuery(filters: SchoolReportingFilters): DwQuery {
        const query = AppMetaData.RoInstructorAssignmentsPerWeek.CreateQueryBuilder();

        const aviatorUser = this.adminManagerService.getAviatorUser();

        query.query.FieldSettings.AggregateQuery = true;

        // Always filter by Flight School
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId));

        // Apply additional filters
        if (filters.courseId) {
            query.addFilterAnd(f => f.Equal(a => a.CourseId, filters.courseId));
        }

        if (filters.lastActivityAfterDate) {
            query.addFilterAnd(f => f.GreaterThanEqual(a => a.AssignedWeekEndingDate, filters.lastActivityAfterDate));
            query.addFilterAnd(f => f.LessThanEqual(a => a.AssignedWeekEndingDate, this.dateTimeUtilServie.getCurrentDateTime()));
        }

        // Add Group by Fields
        query.addFields(f => [f.FlightSchoolId, f.InstructorId]);

        // Add Aggregate Fields
        const attrs = AppMetaData.RoInstructorAssignmentsPerWeek.Attributes;

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Avg,
            FieldAlias: InstructorAssignmentStatAggregateFields.AverageAssignmentsPerWeek,
            FullFieldName: attrs.AssignmentCount.fieldNameWithItemPrefix
        });

        return query.query;
    }

    public getSchoolGroupStudents(groupId: number): Observable<InstructorStudentEntity[]> {

        if (!groupId || groupId <= 0) {
            return of([]);
        }

        const cacheInvalidationTopics: string[] = []

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolGroup.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolGroupStudent.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwSecurityTopics.SECURITY_CHANGED);

        return this.dwCacheService.get(`SchoolGroupStudents`, this.loadAllSchoolGroupStudents(), null, cacheInvalidationTopics)
            .pipe(map(students => {
                return students.filter(s => s.FlightSchoolGroupId === groupId);
            }));
    }

    public getInstructorStudents(instructorId: number): Observable<InstructorStudentEntity[]> {
        if (!instructorId || instructorId <= 0) {
            return of([]);
        }

        const cacheInvalidationTopics: string[] = []

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Instructor.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.InstructorStudent.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwSecurityTopics.SECURITY_CHANGED);

        return this.dwCacheService.get(`InstructorStudents`, this.loadAllInstructorStudents(), null, cacheInvalidationTopics)
            .pipe(map(students => {
                return students.filter(s => s.InstructorId === instructorId);
            }));
    }

    private loadAllSchoolGroupStudents(): Observable<FlightSchoolGroupStudentEntity[]> {
        const query = AppMetaData.FlightSchoolGroupStudent.CreateQueryBuilder();
        const user = this.adminManagerService.getAviatorUser();

        query.addFilterAnd(f => f.Equal(a => AppMetaData.FlightSchoolGroup.Attributes.FlightSchoolId, user.FlightSchoolId));

        return this.dwOrmDataService.executeQuery(query);
    }

    private loadAllInstructorStudents(): Observable<InstructorStudentEntity[]> {
        const query = AppMetaData.InstructorStudent.CreateQueryBuilder();

        const user = this.adminManagerService.getAviatorUser();

        query.addFilterAnd(f => f.Equal(a => AppMetaData.Instructor.Attributes.FlightSchoolId, user.FlightSchoolId));

        return this.dwOrmDataService.executeQuery(query);
    }


    private buildSchoolCourseEnrollmentQuery(filters: SchoolReportingFilters, includeHidden: boolean = false): RoSchoolCourseEnrollmentStatsQueryBuilder {
        const query = AppMetaData.RoSchoolCourseEnrollmentStats.CreateQueryBuilder();

        const aviatorUser = this.adminManagerService.getAviatorUser();

        // Always filter by Flight School
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId));

        if (!includeHidden) {
            query.addFilterAnd(f => f.Equal(a => a.HideInReporting, false));
        }

        // Apply additional filters
        if (filters.courseId) {
            query.addFilterAnd(f => f.Equal(a => a.CourseId, filters.courseId));
        }
        if (filters.studentId) {
            query.addFilterAnd(f => f.Equal(a => a.StudentId, filters.studentId));
        }

        if (filters.instructorStudentIds && filters.instructorStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.StudentId, filters.instructorStudentIds));
        }

        if (filters.groupStudentIds && filters.groupStudentIds.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.StudentId, filters.groupStudentIds));
        }

        if (filters.lastActivityAfterDate) {
            query.addFilterAnd(f => f.GreaterThanEqual(a => a.LastActivityDateTime, filters.lastActivityAfterDate));
        }

        return query;
    }



}